/**
 * W4 (field test 2026-10-16) — HUMAN-APPROVED ROLLOVER: detection, the decision options, and the
 * boundary between routine maintenance and a new decision. Pure.
 *
 * Binding (DECISION_LEDGER §1): neither Core nor Catalyst may convert an unstarted ticket into a
 * rollover. OptiBay detects the end-of-day conflict, asks an authorized person, and only after an
 * approval builds and maintains the chain inside the approved scope. An approval never authorizes a
 * change to a customer promise, overtime, a technician reassignment or a protected appointment.
 *
 *   D-4  options, in this order, NOTHING preselected:
 *          1 Reassign · 2 Approve rollover · 3 Authorize overtime · 4 Reschedule · 5 Leave unresolved
 *   D-6  the allocation boundary is the authorized EOD; the willingness tolerance only decides when
 *        the question is asked
 *   D-7a started work: a NEW carry decision needs a human (same options); an approved chain is
 *        maintained within its scope; a productive timer is never stopped
 *   D-10 OPEN — conservative rule implemented here: recalculation inside the approved scope (approved
 *        work, approved date horizon, no promise / overtime / technician / protected-work change) is
 *        routine; anything outside it is DECISION REQUIRED (nothing written automatically)
 */

export const DECISION_OPTION_ORDER = [
  'reassign',
  'approve_rollover',
  'authorize_overtime',
  'reschedule',
  'leave_unresolved',
] as const;
export type DecisionOption = (typeof DECISION_OPTION_ORDER)[number];

export type EodSubjectKind = 'unstarted' | 'started' | 'terminal';

export interface EodSubjectFacts {
  status?: string | null;
  actuallyStarted?: boolean | null;
  timerStatus?: string | null;
  type?: string | null;
  scheduledStartTime?: string | null;
  isPinned?: boolean | null;
  isLocked?: boolean | null;
  /** Already part of a chain (root with a live continuation, or a continuation). */
  chainGoverned?: boolean | null;
}

const TERMINAL = new Set(['Completed', 'Picked Up', 'Cancelled', 'Deleted', 'Blocked']);

export type Protection = 'waiter' | 'appointment' | 'pinned' | 'locked';

export function classifyEodSubject(t: EodSubjectFacts): { kind: EodSubjectKind; protections: Protection[] } {
  const protections: Protection[] = [];
  if (t.type === 'Waiter') protections.push('waiter');
  if (t.scheduledStartTime) protections.push('appointment');
  if (t.isPinned) protections.push('pinned');
  if (t.isLocked) protections.push('locked');
  if (t.status && TERMINAL.has(t.status)) return { kind: 'terminal', protections };
  const started = t.actuallyStarted === true || t.timerStatus === 'running' || t.timerStatus === 'paused';
  return { kind: started ? 'started' : 'unstarted', protections };
}

export interface EodDetection {
  /** Any projected work past the authorized boundary is an unauthorized schedule. */
  conflict: boolean;
  overrunMin: number;
  /** Past the shop's tolerance: raise / escalate the decision now (D-6). */
  askNow: boolean;
}

export function detectEodConflict(input: {
  projectedFinishMin: number | null;
  boundaryMin: number | null;
  toleranceMin?: number | null;
  /** True when the ticket could not even start before the boundary. */
  noCapacityToday?: boolean;
}): EodDetection {
  if (input.boundaryMin === null || !Number.isFinite(input.boundaryMin)) return { conflict: false, overrunMin: 0, askNow: false };
  const projected = input.projectedFinishMin;
  const known = projected !== null && Number.isFinite(projected);
  if (input.noCapacityToday) {
    return { conflict: true, overrunMin: known ? Math.max(0, Math.round((projected as number) - input.boundaryMin)) : 0, askNow: true };
  }
  if (!known) return { conflict: false, overrunMin: 0, askNow: false };
  const overrunMin = Math.max(0, Math.round((projected as number) - input.boundaryMin));
  const tol = Math.max(0, Math.round(input.toleranceMin ?? 0));
  return { conflict: overrunMin > 0, overrunMin, askNow: overrunMin > tol };
}

export interface OptionContext {
  subject: { kind: EodSubjectKind; protections: Protection[] };
  /** The continuation minutes now, and at worst (nothing done today). */
  continuationNowMin: number;
  continuationWorstMin: number;
  /** Last date the worst-case plan reaches; null when the horizon is exhausted. */
  worstLastDate: string | null;
  /** The rollover plan's final finish breaks the customer's due-out promise. */
  rolloverBreachesDueOut: boolean;
  /** Overtime minutes past the boundary that would finish the work today. */
  overtimeNeededMin: number;
  /** Technicians with capacity today (from the ranking), informational. */
  reassignCandidateCount: number;
  /** A live chain already exists (D-9: replan it, never a parallel chain). */
  existingChain: boolean;
}

export interface DecisionOptionView {
  option: DecisionOption;
  /** 1-based, D-4. */
  position: number;
  enabled: boolean;
  disabledReason: string | null;
  consequence: string;
  /** Promises and protections this option touches (shown, never changed by approval). */
  affects: string[];
}

export const MAX_OVERTIME_MINUTES = 480;

export function buildDecisionOptions(ctx: OptionContext): { options: DecisionOptionView[]; preselected: null } {
  const waiter = ctx.subject.protections.includes('waiter');
  const affects: string[] = [];
  if (ctx.rolloverBreachesDueOut) affects.push('due_out_promise');
  if (ctx.subject.protections.includes('appointment')) affects.push('protected_appointment');
  if (waiter) affects.push('waiter');

  const views: Record<DecisionOption, Omit<DecisionOptionView, 'position' | 'option'>> = {
    reassign: {
      enabled: ctx.subject.kind !== 'terminal',
      disabledReason: ctx.subject.kind === 'terminal' ? 'ticket_not_live' : null,
      consequence: ctx.reassignCandidateCount > 0
        ? `move to another technician with capacity today (${ctx.reassignCandidateCount} available)`
        : 'move to another technician (none shows free capacity today — preview first)',
      affects: [],
    },
    approve_rollover: (() => {
      let reason: string | null = null;
      if (ctx.subject.kind === 'terminal') reason = 'ticket_not_live';
      else if (waiter) reason = 'waiter_cannot_carry';
      // B-W13: a customer appointment is a protected commitment like a Waiter — never carried
      // (decide_rollover_conversion refuses it server-side: APPOINTMENT_CANNOT_CARRY).
      else if (ctx.subject.protections.includes('appointment')) reason = 'appointment_cannot_carry';
      else if (ctx.rolloverBreachesDueOut) reason = 'breaks_due_out_promise';
      else if (ctx.worstLastDate === null) reason = 'no_capacity_in_horizon';
      return {
        enabled: reason === null,
        disabledReason: reason,
        consequence: ctx.existingChain
          ? `re-plan the existing chain (about ${ctx.continuationNowMin} min continues; up to ${ctx.continuationWorstMin} min if no work happens today)`
          : `about ${ctx.continuationNowMin} min continues on the next working day; up to ${ctx.continuationWorstMin} min if no work happens today${ctx.worstLastDate ? ` (latest ${ctx.worstLastDate})` : ''}`,
        affects: affects.filter((a) => a !== 'due_out_promise' || ctx.rolloverBreachesDueOut),
      };
    })(),
    authorize_overtime: (() => {
      const too = ctx.overtimeNeededMin > MAX_OVERTIME_MINUTES;
      const none = ctx.overtimeNeededMin <= 0;
      return {
        enabled: ctx.subject.kind !== 'terminal' && !too && !none,
        disabledReason: ctx.subject.kind === 'terminal' ? 'ticket_not_live' : too ? 'overtime_exceeds_maximum' : none ? 'no_overtime_needed' : null,
        consequence: `authorize ${Math.max(0, ctx.overtimeNeededMin)} min past the end of day for this ticket only`,
        affects: [],
      };
    })(),
    reschedule: {
      enabled: ctx.subject.kind !== 'terminal',
      disabledReason: ctx.subject.kind === 'terminal' ? 'ticket_not_live' : null,
      consequence: 'choose a later date or time (a promise change stays a separate, explicit action)',
      affects: ctx.rolloverBreachesDueOut ? ['due_out_promise'] : [],
    },
    leave_unresolved: {
      enabled: true,
      disabledReason: null,
      consequence: 'stays Decision Required; at the end of the day it becomes Needs Placement (never carried, never completed)',
      affects: [],
    },
  };
  return {
    options: DECISION_OPTION_ORDER.map((option, i) => ({ option, position: i + 1, ...views[option] })),
    preselected: null,
  };
}

// ─── B-W3: the viewer's overtime authority (who may authorize stays the server's rule) ─────────────────────────────

/** Disabled reason for "Authorize overtime" when the viewing person is not admitted by the server's predicate. */
export const NOT_AUTHORIZED_FOR_OVERTIME = 'not_authorized_for_overtime';

/**
 * The options stored on a proposal are computed once, for nobody in particular (eodDecisionDetection). Overtime is
 * authorized by `authorize_ticket_overtime`, which admits only `can_authorize_overtime` (20270206060200) — a narrower
 * set than the D-1 rollover approvers. So a writer was offered "Authorize overtime" and then refused by the server.
 *
 * `mayAuthorizeOvertime` is the server's own answer for the viewing person (`can_i_authorize_overtime` → the same
 * `can_authorize_overtime` predicate; single source — the client never decides who may). This only DISPLAYS that answer:
 *   - true  → unchanged;
 *   - false → "Authorize overtime" stays in its D-4 position, disabled with NOT_AUTHORIZED_FOR_OVERTIME (an option
 *             that is already disabled keeps its own reason);
 *   - null  → unknown (the read failed): unchanged — the server still refuses on submit, as before.
 * Order is never changed and nothing is selected.
 */
export function applyViewerOvertimeAuthority<T extends { option: string; enabled: boolean; disabledReason: string | null }>(
  options: readonly T[], mayAuthorizeOvertime: boolean | null,
): T[] {
  if (mayAuthorizeOvertime !== false) return [...options];
  return options.map((o) => (o.option === 'authorize_overtime' && o.enabled
    ? { ...o, enabled: false, disabledReason: NOT_AUTHORIZED_FOR_OVERTIME }
    : o));
}

// ─── routine maintenance vs a new decision (D-10, conservative) ─────────────

export interface ApprovedScope {
  /** The work (minutes, assignee units) the approver saw as the worst case. */
  approvedWorkMin: number;
  /** The last date of the worst-case plan the approver saw. */
  approvedLastDate: string;
  technicianId: string;
  /** The due-out promise at approval (ISO) — approval never changes it. */
  dueOut?: string | null;
}

export interface ChainPlanFacts {
  remainingWorkMin: number | null;
  lastDate: string | null;
  technicianIds: string[];
  /** Final finish instant (ISO) of the plan. */
  finalFinishIso: string | null;
  requiresOvertime: boolean;
  displacesProtected: boolean;
  complete: boolean;
}

export type DecisionRequiredReason =
  | 'work_beyond_approved'
  | 'date_beyond_approved_horizon'
  | 'breaks_due_out_promise'
  | 'requires_overtime'
  | 'technician_change'
  | 'displaces_protected_work'
  | 'no_capacity_in_horizon'
  | 'remaining_unknown';

export function classifyChainMaintenance(scope: ApprovedScope, plan: ChainPlanFacts): { routine: boolean; reasons: DecisionRequiredReason[] } {
  const reasons: DecisionRequiredReason[] = [];
  if (plan.remainingWorkMin === null) reasons.push('remaining_unknown');
  else if (plan.remainingWorkMin > scope.approvedWorkMin) reasons.push('work_beyond_approved');
  if (!plan.complete) reasons.push('no_capacity_in_horizon');
  else if (plan.lastDate && plan.lastDate > scope.approvedLastDate) reasons.push('date_beyond_approved_horizon');
  if (scope.dueOut && plan.finalFinishIso && Date.parse(plan.finalFinishIso) > Date.parse(scope.dueOut)) reasons.push('breaks_due_out_promise');
  if (plan.requiresOvertime) reasons.push('requires_overtime');
  if (plan.technicianIds.some((t) => t !== scope.technicianId)) reasons.push('technician_change');
  if (plan.displacesProtected) reasons.push('displaces_protected_work');
  return { routine: reasons.length === 0, reasons };
}

/** Who may approve (D-1): exactly the server predicate's admissions, for client display only. */
export const APPROVER_LEGACY_ROLES = ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'] as const;
